import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Job, JobListQuery, JobScheduleInput } from '@fsm/shared';
import { api } from './api';

/**
 * Query keys in one place.
 *
 * The dispatch board invalidates jobs from four different interactions; having
 * the key shape written once means a drag and a status change cannot end up
 * invalidating subtly different things.
 */
export const keys = {
  me: ['me'] as const,
  users: (role?: string) => ['users', role ?? 'all'] as const,
  customers: (q?: string) => ['customers', q ?? ''] as const,
  customer: (id: string) => ['customer', id] as const,
  priceBook: (q?: string) => ['pricebook', q ?? ''] as const,
  jobs: (query: Partial<JobListQuery>) => ['jobs', query] as const,
  job: (id: string) => ['job', id] as const,
  estimates: (status?: string) => ['estimates', status ?? 'all'] as const,
  estimate: (id: string) => ['estimate', id] as const,
  invoices: (filter?: string) => ['invoices', filter ?? 'all'] as const,
  invoice: (id: string) => ['invoice', id] as const,
  recurrences: ['recurrences'] as const,
  mailSettings: ['settings', 'email'] as const,
  companyProfile: ['settings', 'company'] as const,
  catalog: (kind: string) => ['catalog', kind] as const,
  search: (q: string) => ['search', q] as const,
  reports: {
    revenue: (months: number) => ['reports', 'revenue', months] as const,
    jobsByStatus: ['reports', 'jobs-by-status'] as const,
    receivables: ['reports', 'receivables'] as const,
  },
};

/** Every job-shaped surface at once, after any write that could move a job. */
export function invalidateJobWorld(qc: QueryClient, jobId?: string): void {
  void qc.invalidateQueries({ queryKey: ['jobs'] });
  void qc.invalidateQueries({ queryKey: ['reports'] });
  if (jobId) void qc.invalidateQueries({ queryKey: keys.job(jobId) });
}

export function useTechs() {
  return useQuery({
    queryKey: keys.users('tech'),
    queryFn: () => api.users.techs(),
    staleTime: 5 * 60_000,
    select: (data) => data.items,
  });
}

export function useJobs(query: Partial<JobListQuery>, enabled = true) {
  return useQuery({
    queryKey: keys.jobs(query),
    queryFn: () => api.jobs.list(query),
    enabled,
    select: (data) => data.items,
    // The board is shared; a dispatcher should see a colleague's drag quickly.
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: keys.job(id ?? ''),
    queryFn: () => api.jobs.get(id!),
    enabled: Boolean(id),
  });
}

/**
 * One invoice, by id.
 *
 * The job page needs it for the payment history and the invoice row: a job
 * carries only `invoiceId`, and the balance, the totals and the payments are
 * the invoice's to report — recomputing any of them here would be a second
 * copy of a rule that already lives on the server.
 */
export function useInvoice(id: string | null | undefined) {
  return useQuery({
    queryKey: keys.invoice(id ?? ''),
    queryFn: () => api.invoices.get(id!),
    enabled: Boolean(id),
  });
}

export function useEstimate(id: string | null | undefined) {
  return useQuery({
    queryKey: keys.estimate(id ?? ''),
    queryFn: () => api.estimates.get(id!),
    enabled: Boolean(id),
  });
}

export interface RescheduleVariables {
  jobId: string;
  input: JobScheduleInput;
  /** The list query whose cache should be patched optimistically. */
  listKey: readonly unknown[];
}

/**
 * Optimistic reschedule.
 *
 * The block must land where it was dropped instantly — a dispatcher dragging
 * twenty jobs cannot wait for a round trip each time. The server still decides:
 * a 409 rolls the cache back and surfaces the conflict.
 */
export function useReschedule(onConflict?: (message: string, details: unknown) => void) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, input }: RescheduleVariables) => api.jobs.schedule(jobId, input),

    onMutate: async ({ jobId, input, listKey }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const previous = qc.getQueryData<{ items: Job[] }>(listKey);

      qc.setQueryData<{ items: Job[] }>(listKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          items: current.items.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  assignedTechId: input.assignedTechId,
                  scheduledStart: input.scheduledStart,
                  scheduledEnd: input.scheduledEnd,
                  status:
                    input.assignedTechId && input.scheduledStart
                      ? job.status === 'unscheduled'
                        ? 'scheduled'
                        : job.status
                      : 'unscheduled',
                }
              : job,
          ),
        };
      });

      return { previous, listKey };
    },

    onError: (error, _vars, context) => {
      if (context?.previous) qc.setQueryData(context.listKey, context.previous);
      const err = error as { status?: number; message?: string; details?: unknown };
      if (err.status === 409) {
        onConflict?.(err.message ?? 'That move conflicts with another job', err.details);
      }
    },

    onSettled: (_data, _error, variables) => {
      void qc.invalidateQueries({ queryKey: variables.listKey });
      invalidateJobWorld(qc, variables.jobId);
    },
  });
}
